import { FieldArray, useFormikContext, getIn } from "formik";
import { ReactNode } from "react";
import { FaTrash, FaPlus } from "react-icons/fa";

interface FormikFieldArrayProps<T = any> {
  name: string;
  label?: string;
  pushItem: T; // Default object for new item
  renderItem: (item: T, index: number) => ReactNode;
  renderAddButton?: (push: (obj: T) => void) => ReactNode;
}

const FormikFieldArray = <T,>({
  name,
  label,
  pushItem,
  renderItem,
  renderAddButton,
}: FormikFieldArrayProps<T>) => {
  const { values, errors, touched } = useFormikContext<any>();
  const items = getIn(values, name) || [];
  const error = getIn(errors, name);
  const isTouched = getIn(touched, name);

  return (
    <div className="w-full flex flex-col gap-4">
      {label && (
        <label className="block text-sm font-semibold text-gray-700">
          {label}
        </label>
      )}

      <FieldArray name={name}>
        {({ push, remove }) => (
          <div className="flex flex-col gap-4">
            {/* List Items */}
            <div className="flex flex-col gap-4">
              {items.map((item: T, index: number) => (
                <div
                  key={index}
                  className="relative p-4 border rounded-lg bg-gray-50 border-gray-200"
                >
                  <button
                    type="button"
                    onClick={() => remove(index)}
                    className="absolute top-2 right-2 p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors z-10"
                    title="Remove item"
                  >
                    <FaTrash size={14} />
                  </button>

                  <div className="w-full pr-8">
                    {/* pr-8 to avoid overlap with delete button on small screens if content is long */}
                    {renderItem(item, index)}
                  </div>
                </div>
              ))}
            </div>

            {/* Add Button */}
            <div>
              {renderAddButton ? (
                renderAddButton(push)
              ) : (
                <button
                  type="button"
                  onClick={() => push(pushItem)}
                  className="w-full py-2 border-2 border-dashed border-gray-300 text-gray-500 hover:border-primary hover:text-primary rounded-lg flex items-center justify-center gap-2 transition-colors duration-200"
                >
                  <FaPlus size={14} />
                  <span className="font-medium">Ajouter un élément</span>
                </button>
              )}
            </div>
          </div>
        )}
      </FieldArray>

      {/* Error Message */}
      {typeof error === "string" && isTouched && (
        <div className="text-sm text-red-500 mt-1">
          {error}
        </div>
      )}
    </div>
  );
};

export default FormikFieldArray;
